import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Seo } from "../components/Seo";
import { PromoPopup } from "../components/PromoPopup";
import { SiteNav } from "../components/SiteNav";
import { SiteFooter } from "../components/SiteFooter";
import { Phone, MessageSquare, Globe, Shield, Zap, Check, PhoneCall, XCircle, ArrowUpRight, Info, Smartphone } from "lucide-react";

const F1_IMG = "https://images.unsplash.com/photo-1711535767116-6bc0d7948fb9?crop=entropy&cs=srgb&fm=jpg&auto=format&fit=crop&w=1200&q=85";
const F2_IMG = "https://images.unsplash.com/photo-1620228885847-9eab2a1adddc?auto=format&fit=crop&w=1200&q=80";
const HERO = "https://static.prod-images.emergentagent.com/jobs/364e14e3-15fd-48b2-b3bf-07611febddfe/images/42be3215e5951065544e7575c254149fdc565b775b99f529b3550446f965b956.jpeg";

const INCLUDED = [
    { icon: Phone, t: "A real US phone number", d: "Pick your area code. It rings and texts like any other number." },
    { icon: MessageSquare, t: "Texting (SMS)", d: "Monthly text allowance by plan — 200, 1,000 or 5,000." },
    { icon: PhoneCall, t: "Calling + voicemail", d: "Make and take calls in the app, with your own voicemail greeting." },
    { icon: Smartphone, t: "Works on the phone you have", d: "Runs in the FlexLine app over Wi-Fi or your current data. No SIM swap." },
];

export default function SecondNumber() {
    const { user, startLogin } = useAuth();
    const navigate = useNavigate();
    const [billingCycle, setBillingCycle] = React.useState("monthly");
    const onCTA = () => (user ? navigate("/dashboard") : startLogin());
    return (
        <div className="min-h-screen bg-cream text-zinc-900" data-testid="second-number-page">
            <Seo title="Second Phone Number — Private Texting & Calling App · FlexLine Mobile"
                description="Get a second phone number for texting and calling in the FlexLine app. Plans from $7.99/mo. Works on your current phone over Wi-Fi or data — not a cell plan."
                path="/second-number" />
            <PromoPopup />
            <SiteNav />
            <section className="relative overflow-hidden border-b border-[#DDD6FE]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6 animate-[fadeIn_0.6s_ease-out]">
                        <p className="font-mono text-[11px] uppercase tracking-wider text-[#B45309] mb-5">— Product 02 · Second phone number</p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[0.95] tracking-tight">
                            A second number,<br /><span className="font-serif-italic text-[#4C1D95]">on the phone you already have.</span>
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-zinc-700 max-w-xl leading-relaxed">
                            Keep your personal number private. Get an extra line for business, dating, selling online or travel —
                            texting and calling run inside the FlexLine app.
                        </p>
                        <div className="mt-6 flex items-start gap-2 text-sm bg-white border border-[#DDD6FE] rounded-sm p-4 max-w-xl" data-testid="second-number-not-cell-note">
                            <Info className="w-4 h-4 text-[#4C1D95] mt-0.5 shrink-0" />
                            <span>This is <b>not</b> a cell phone plan — there's no eSIM or mobile data. Want a full wireless line? See <a href="/wireless" className="link-underline text-[#4C1D95] font-medium">wireless plans</a>.</span>
                        </div>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} data-testid="second-number-see-plans-btn"
                                className="bg-[#4C1D95] text-white hover:bg-[#2E1065] rounded-full h-12 px-7">See plans from $7.99/mo</Button>
                            <Button onClick={onCTA} variant="outline" data-testid="second-number-get-btn" className="border-[#DDD6FE] rounded-full h-12 px-7">
                                {user ? "Open app" : "Get my number"} <ArrowUpRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <img src={HERO} alt="Second phone number app on a smartphone" className="w-full rounded-2xl border border-[#DDD6FE] shadow-[0_30px_80px_-30px_rgba(76,29,149,0.45)] object-cover aspect-[3/2]" />
                    </div>
                </div>
            </section>

            <section className="border-b border-[#DDD6FE] bg-white" data-testid="second-number-included">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-6">— What's included in every second-number plan</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {INCLUDED.map(({ icon: Icon, t, d }) => (
                            <div key={t} className="border border-[#DDD6FE] rounded-sm p-5 hover:-translate-y-0.5 transition-transform">
                                <Icon className="w-5 h-5 text-emerald-deep mb-3" />
                                <p className="font-medium">{t}</p>
                                <p className="text-sm text-zinc-600 mt-1">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features bento */}
            <section id="features" data-testid="second-number-features" className="border-b border-[#DDD6FE]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
                    <div className="grid lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                                — Why Flex Line
                            </p>
                            <h2 className="font-display text-5xl md:text-6xl font-light leading-[0.95] tracking-tight">
                                Everything you want.
                                <br />
                                <span className="font-serif-italic font-normal">Nothing you don&apos;t.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 text-zinc-700 leading-relaxed pt-3 text-lg">
                            A clean app, a real number, and prices that don&apos;t make you flinch.
                            We&apos;re built by the team that got tired of TextNow&apos;s ads and
                            Google Voice&apos;s missing features.
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-6 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                        <Card className="md:col-span-4 p-10">
                            <div className="flex items-start justify-between mb-10">
                                <div>
                                    <p className="font-mono text-[11px] uppercase text-zinc-500">01 — Texting</p>
                                    <h3 className="font-display text-3xl font-light mt-3 tracking-tight">
                                        Real SMS that arrives,
                                        <br />
                                        <span className="font-serif-italic">every time.</span>
                                    </h3>
                                </div>
                                <MessageSquare className="w-6 h-6 text-emerald-deep" />
                            </div>
                            <img
                                src={F1_IMG}
                                alt=""
                                className="w-full h-64 object-cover rounded-sm border border-[#DDD6FE] grayscale-[15%]"
                            />
                        </Card>
                        <Card className="md:col-span-2 p-10">
                            <Globe className="w-6 h-6 text-emerald-deep mb-10" />
                            <p className="font-mono text-[11px] uppercase text-zinc-500">02 — Pick</p>
                            <h3 className="font-display text-3xl font-light mt-3 leading-tight tracking-tight">
                                Choose your area code.
                            </h3>
                            <p className="text-sm text-zinc-600 mt-5 leading-relaxed">
                                NYC, LA, Miami, or your hometown — the number you want, the first time.
                            </p>
                        </Card>
                        <Card className="md:col-span-2 p-10">
                            <Zap className="w-6 h-6 text-emerald-deep mb-10" />
                            <p className="font-mono text-[11px] uppercase text-zinc-500">03 — Instant</p>
                            <h3 className="font-display text-3xl font-light mt-3 leading-tight tracking-tight">
                                Active in 30 seconds.
                            </h3>
                            <p className="text-sm text-zinc-600 mt-5 leading-relaxed">
                                Sign in, claim a number, start texting. No app downloads required.
                            </p>
                        </Card>
                        <Card className="md:col-span-4 p-10">
                            <div className="flex items-start justify-between mb-10">
                                <div>
                                    <p className="font-mono text-[11px] uppercase text-zinc-500">04 — Voice</p>
                                    <h3 className="font-display text-3xl font-light mt-3 tracking-tight">
                                        Calls, voicemail, the works.
                                    </h3>
                                </div>
                                <PhoneCall className="w-6 h-6 text-emerald-deep" />
                            </div>
                            <img
                                src={F2_IMG}
                                alt=""
                                className="w-full h-64 object-cover rounded-sm border border-[#DDD6FE] grayscale-[20%]"
                            />
                        </Card>
                        <Card className="md:col-span-6 p-10 md:p-16">
                            <div className="grid md:grid-cols-2 gap-10 items-center">
                                <div>
                                    <Shield className="w-6 h-6 text-emerald-deep mb-6" />
                                    <p className="font-mono text-[11px] uppercase text-zinc-500">05 — Privacy</p>
                                    <h3 className="font-display text-4xl font-light mt-3 leading-tight tracking-tight">
                                        Keep your real number
                                        <br />
                                        <span className="font-serif-italic">to yourself.</span>
                                    </h3>
                                    <p className="text-zinc-700 mt-5 leading-relaxed text-base">
                                        Burn a number when you&apos;re done. Get a new one for the
                                        next thing. Your personal line stays personal — for friends,
                                        family, and your bank.
                                    </p>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {Array.from({ length: 9 }).map((_, i) => (
                                        <div
                                            key={i}
                                            className="aspect-square border border-[#DDD6FE] bg-cream/40 rounded-sm grid place-items-center"
                                        >
                                            <Phone className="w-5 h-5 text-zinc-400" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="border-b border-[#DDD6FE]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                            — Pricing
                        </p>
                        <h2 className="font-display text-5xl md:text-6xl font-light leading-[0.95] tracking-tight">
                            Less than your
                            <br />
                            <span className="font-serif-italic font-normal">morning coffee.</span>
                        </h2>
                        <p className="text-zinc-700 mt-6 text-lg">
                            Second phone number plans — texting &amp; calling inside the FlexLine app. Paid upfront, cancel any time.
                        </p>
                        <div className="mt-5 inline-flex items-center gap-2 bg-[#4C1D95] text-white px-4 py-2 rounded-full text-sm font-medium" data-testid="trial-pill">
                            <span className="w-2 h-2 rounded-full bg-[#D97706]" /> Month-to-month plans — no contract, cancel anytime
                        </div>
                    </div>

                    {/* Billing toggle */}
                    <div className="flex justify-center mb-12">
                        <div className="inline-flex items-center border border-[#DDD6FE] rounded-sm bg-paper p-1" data-testid="billing-toggle">
                            <button
                                data-testid="billing-toggle-monthly"
                                onClick={() => setBillingCycle("monthly")}
                                className={`px-5 py-2 text-sm rounded-sm transition-colors ${
                                    billingCycle === "monthly"
                                        ? "bg-espresso text-[#FAF5FF]"
                                        : "text-zinc-700 hover:bg-cream"
                                }`}
                            >
                                Monthly
                            </button>
                            <button
                                data-testid="billing-toggle-yearly"
                                onClick={() => setBillingCycle("yearly")}
                                className={`px-5 py-2 text-sm rounded-sm transition-colors flex items-center gap-2 ${
                                    billingCycle === "yearly"
                                        ? "bg-espresso text-[#FAF5FF]"
                                        : "text-zinc-700 hover:bg-cream"
                                }`}
                            >
                                Annual
                                <span className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${
                                    billingCycle === "yearly"
                                        ? "text-[#C4B5FD] border-[#C4B5FD]"
                                        : "text-emerald-deep border-emerald-deep"
                                }`}>
                                    Save 20%
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                        <PricingCard
                            name="Lite"
                            monthly={7.99}
                            yearly={77}
                            cycle={billingCycle}
                            features={["1 phone number included", "Extra numbers $4.99 one-time each", "200 SMS / month", "Voice + voicemail", "Ad-free texting"]}
                            cta="Get started"
                            testid="pricing-starter-cta"
                            onClick={onCTA}
                        />
                        <PricingCard
                            highlight
                            name="Plus"
                            monthly={17.99}
                            yearly={173}
                            cycle={billingCycle}
                            features={["Up to 2 phone numbers (extra $4.99 one-time)", "1,000 SMS / month", "Priority voice routing", "Free local calling", "Custom voicemail greeting"]}
                            cta="Get started"
                            testid="pricing-pro-cta"
                            onClick={onCTA}
                        />
                        <PricingCard
                            name="Pro"
                            monthly={31.99}
                            yearly={307}
                            cycle={billingCycle}
                            features={["1 phone number included", "Extra numbers $4.99 one-time each", "5,000 SMS / month", "International calling", "Concierge support", "API access"]}
                            cta="Get started"
                            testid="pricing-enterprise-cta"
                            onClick={onCTA}
                        />
                    </div>
                    <p className="text-center text-xs text-zinc-500 mt-6 font-mono">
                        Every plan includes your first number. Extra numbers $4.99 one-time each.                    </p>
                </div>
            </section>

            {/* Compare */}
            <section id="compare" className="border-b border-[#DDD6FE]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
                    <div className="grid lg:grid-cols-12 gap-12 mb-12">
                        <div className="lg:col-span-6">
                            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                                — Comparison
                            </p>
                            <h2 className="font-display text-5xl md:text-6xl font-light leading-[0.95] tracking-tight">
                                Flex Line
                                <br />
                                <span className="font-serif-italic font-normal">vs the rest.</span>
                            </h2>
                        </div>
                    </div>
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[#DDD6FE] bg-cream/60">
                                    <th className="text-left px-6 py-4 font-mono text-[10px] uppercase text-zinc-500">Feature</th>
                                    <th className="px-6 py-4 font-display text-xl">Flex Line</th>
                                    <th className="px-6 py-4 font-mono text-sm text-zinc-500">TextNow</th>
                                    <th className="px-6 py-4 font-mono text-sm text-zinc-500">Google Voice</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm">
                                <Row label="Starting price" yes="$7.99/mo" textnow="$9.99/mo" gv="Free*" />
                                <Row label="Real, dialable US number" yes={<Check className="w-4 h-4 text-emerald-deep mx-auto" />} textnow={<Check className="w-4 h-4 text-zinc-400 mx-auto" />} gv={<Check className="w-4 h-4 text-zinc-400 mx-auto" />} />
                                <Row label="Ad-free texting & calls" yes={<Check className="w-4 h-4 text-emerald-deep mx-auto" />} textnow={<XCircle className="w-4 h-4 text-zinc-300 mx-auto" />} gv={<Check className="w-4 h-4 text-zinc-400 mx-auto" />} />
                                <Row label="Multiple numbers per account" yes={<Check className="w-4 h-4 text-emerald-deep mx-auto" />} textnow={<XCircle className="w-4 h-4 text-zinc-300 mx-auto" />} gv={<XCircle className="w-4 h-4 text-zinc-300 mx-auto" />} />
                                <Row label="Web-first dashboard" yes={<Check className="w-4 h-4 text-emerald-deep mx-auto" />} textnow={<XCircle className="w-4 h-4 text-zinc-300 mx-auto" />} gv={<Check className="w-4 h-4 text-zinc-400 mx-auto" />} />
                                <Row label="Use abroad" yes={<Check className="w-4 h-4 text-emerald-deep mx-auto" />} textnow={<Check className="w-4 h-4 text-zinc-400 mx-auto" />} gv={<XCircle className="w-4 h-4 text-zinc-300 mx-auto" />} last />
                            </tbody>
                        </table>
                    </div>
                    <p className="text-xs text-zinc-500 mt-3 font-mono">* Google Voice requires existing US number to set up.</p>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}

function Card({ children, className = "" }) {
    return (
        <div className={`bg-paper border-0 rounded-none transition-colors hover:bg-[#F5F0FF] ${className}`}>
            {children}
        </div>
    );
}

function PricingCard({ name, monthly, yearly, cycle, features, cta, highlight, onClick, testid }) {
    const isYearly = cycle === "yearly";
    const price = isYearly ? yearly : monthly;
    const period = isYearly ? "year" : "month";
    const monthlyEquivalent = isYearly ? (yearly / 12).toFixed(2) : null;
    const fullYearMonthly = (monthly * 12).toFixed(2);
    return (
        <div className={`p-12 ${highlight ? "bg-espresso text-[#FAF5FF]" : "bg-paper"}`}>
            <div className="flex items-center justify-between">
                <p className={`font-mono text-[11px] uppercase ${highlight ? "text-[#C4B5FD]" : "text-zinc-500"}`}>
                    — {name}
                </p>
                {highlight && (
                    <span className="font-mono text-[10px] uppercase text-[#C4B5FD] border border-[#C4B5FD] px-2 py-0.5 rounded-sm">
                        Most popular
                    </span>
                )}
            </div>
            <div className="flex items-end gap-1 mt-6">
                <span className="font-display text-6xl font-light tracking-tight">${price}</span>
                <span className={`text-sm pb-3 ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>/ {period}</span>
            </div>
            {isYearly && (
                <div className="mt-2 flex items-center gap-2">
                    <span className={`font-mono text-xs ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>
                        ${monthlyEquivalent}/mo equivalent
                    </span>
                    <span className={`font-mono text-[10px] line-through ${highlight ? "text-zinc-500" : "text-zinc-400"}`}>
                        ${fullYearMonthly}
                    </span>
                </div>
            )}
            <ul className="mt-10 space-y-3.5">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 ${highlight ? "text-[#C4B5FD]" : "text-emerald-deep"}`} />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <Button
                data-testid={testid}
                onClick={onClick}
                className={`mt-12 w-full rounded-sm h-11 ${
                    highlight
                        ? "bg-[#FAF5FF] text-espresso hover:bg-white"
                        : "bg-espresso text-[#FAF5FF] hover:bg-black"
                }`}
            >
                {cta}
            </Button>
        </div>
    );
}

function Row({ label, yes, textnow, gv, last }) {
    return (
        <tr className={last ? "" : "border-b border-[#DDD6FE]/60"}>
            <td className="px-6 py-4 text-sm font-medium">{label}</td>
            <td className="px-6 py-4 text-center font-mono text-sm">{yes}</td>
            <td className="px-6 py-4 text-center font-mono text-sm text-zinc-500">{textnow}</td>
            <td className="px-6 py-4 text-center font-mono text-sm text-zinc-500">{gv}</td>
        </tr>
    );
}

